import React from 'react'
import { Character } from 'types/account'
import { flags } from 'config'
import UserContextModel from 'models/UserContextModel'
import { SimulationResponseType, simulateDonation } from './simulationService'
import { ValidationErrors } from '../donation-form/donation-service'
import { PlexForGoodTranslations } from '../../translations'
import { DonationCampaign } from '../donation-form/types'

/**
 * Debug hook for Plex-for-Good donation functionality
 * Only active when in development mode
 */
export interface DebugValues {
    campaign?: { id: string; name?: string } | null
    plexAmount?: number
    plexBalance?: UserContextModel['plexBalance']
    character?: { id: number; name: string } | null
    charactersTotal?: number
    errorCode?: string | null
}

interface UseDebugFeaturesProps {
    userToken: string
    setDonationStatus: (status: 'idle' | 'success' | 'error') => void
    setErrorMessage: (message: string | null) => void
    setErrorCode: (code: string | null) => void
    setFieldErrors: (errors: ValidationErrors) => void
    setLoading: (loading: boolean) => void
    updatePlex: (token: string) => void
    setCharacters: (characters: Character[]) => void
    setSelectedCharacterId: (id?: number) => void
    setDonationAmount: (amount: number) => void
    translations?: PlexForGoodTranslations
    // Current values for debug display
    selectedCampaign: DonationCampaign | null
    donationAmount: number
    plexBalance: UserContextModel['plexBalance']
    characters: Character[]
    selectedCharacterId?: number
    errorCode: string | null
}

// Define the DebugFeatures interface as a plain type
export interface DebugFeatures {
    simulatedResponse: SimulationResponseType
    setSimulatedResponse: (type: SimulationResponseType) => void
    isUsingRealCharacters: boolean
    handleSimulateCharacters: (count: number) => void
    handleSimulateDonation: (
        type: Exclude<SimulationResponseType, null>
    ) => void
    resetForm: () => void
    shouldUseSimulation: boolean
    debugValues: DebugValues
}

export const useDebugFeatures = (
    props: UseDebugFeaturesProps
): DebugFeatures | null => {
    // Initialize hooks regardless of condition to satisfy rules-of-hooks
    const [
        simulatedResponse,
        setSimulatedResponse,
    ] = React.useState<SimulationResponseType>(null)
    const [isUsingRealCharacters, setIsUsingRealCharacters] = React.useState(
        true
    )

    // If debug is disabled, return null
    if (!flags.isDevelopment) return null

    const {
        userToken,
        setDonationStatus,
        setErrorMessage,
        setErrorCode,
        setFieldErrors,
        setLoading,
        updatePlex,
        setCharacters,
        setSelectedCharacterId,
        setDonationAmount,
        translations,
        // Current values
        selectedCampaign,
        donationAmount,
        plexBalance,
        characters,
        selectedCharacterId,
        errorCode,
    } = props

    // Character simulation handler
    const handleSimulateCharacters = (count: number): void => {
        // Reset selected character when changing character count
        setSelectedCharacterId(undefined)

        if (count === 0) {
            setCharacters([])
            setIsUsingRealCharacters(false)
            return
        }

        // Generate simulated characters with more reasonable name lengths
        const mockCharacters: Character[] = Array.from(
            { length: count },
            (_, i) => {
                // Use more realistic EVE-style character names
                const names = [
                    'Adar Blackroot',
                    'Nova Valentis',
                    'Jax Meridian',
                ]

                return {
                    id: 100000 + i,
                    name: names[i],
                    corporationId: 10000,
                    corporationName: 'Test Corporation',
                    securityStatus: 0,
                    gender: 'male',
                    raceId: 1,
                    raceName: 'Test Race',
                    bloodlineId: 1,
                    bloodlineName: 'Test Bloodline',
                    ancestryId: 1,
                    ancestryName: 'Test Ancestry',
                    orderPosition: i,
                }
            }
        )

        setCharacters(mockCharacters)
        setIsUsingRealCharacters(false)
    }

    // Simulation handler
    const handleSimulateDonation = (
        type: Exclude<SimulationResponseType, null>
    ): void => {
        simulateDonation(
            type,
            // onStart callback
            () => {
                setDonationStatus('idle')
                setErrorMessage(null)
                setErrorCode(null)
                setFieldErrors({})
                setLoading(true)
            },
            // onComplete callback
            (success, errors = {}, message, errCode) => {
                setDonationStatus(success ? 'success' : 'error')
                if (success) {
                    updatePlex(userToken)
                } else {
                    if (message) {
                        setErrorMessage(message)
                    }
                    if (errCode) {
                        setErrorCode(errCode)
                        console.log('Error code detected:', errCode)
                    }
                    setFieldErrors(errors)
                }
                setLoading(false)
            },
            translations
        )
    }

    const resetForm = (): void => {
        setDonationStatus('idle')
        setErrorMessage(null)
        setErrorCode(null)
        setFieldErrors({})
        setDonationAmount(0)
        setSelectedCharacterId(undefined)
        setSimulatedResponse(null)
        setIsUsingRealCharacters(true) // Reset to use real characters
    }

    // Debug values for display
    const debugValues = {
        campaign: selectedCampaign
            ? {
                  id: selectedCampaign.id,
                  name: selectedCampaign.name,
              }
            : null,
        plexAmount: donationAmount,
        plexBalance,
        charactersTotal: characters.length,
        character: selectedCharacterId
            ? characters.find((char) => char.id === selectedCharacterId) || null
            : null,
        errorCode,
    }

    return {
        simulatedResponse,
        setSimulatedResponse,
        isUsingRealCharacters,
        handleSimulateCharacters,
        handleSimulateDonation,
        resetForm,
        shouldUseSimulation: simulatedResponse !== null,
        debugValues,
    }
}
